import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import api from "../../api";
import { queuedMessage, getQueuedScores } from "../../offlineQueue";

export default function TeamSingleBallScoreEntry() {
  const [event, setEvent] = useState(null);
  const [holes, setHoles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  useEffect(() => { loadAll(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const loadAll = async () => {
    try {
      const eventRes = await api.get("/events/current");
      if (!eventRes.data) { setLoading(false); return; }
      setEvent(eventRes.data);

      if (eventRes.data.course_id) {
        const holesRes = await api.get("/courses/" + eventRes.data.course_id + "/holes");
        setHoles(holesRes.data);
      }

      // Build teams from groups
      const groupRes = await api.get("/groups");
      const players = (groupRes.data.players || []).filter(p => p.playing);
      const teamsRes = await api.get("/teams");
      const teamsList = teamsRes.data.teams || [];

      // Group players by grp, then by team
      const grpMap = {};
      players.forEach(p => {
        if (!grpMap[p.grp]) grpMap[p.grp] = {};
        const tid = p.team_id || 0;
        if (!grpMap[p.grp][tid]) grpMap[p.grp][tid] = [];
        grpMap[p.grp][tid].push(p);
      });

      const teamsArr = [];
      Object.entries(grpMap).sort((a,b) => parseInt(a[0])-parseInt(b[0])).forEach(([grp, teamGroups]) => {
        Object.entries(teamGroups).forEach(([tid, members]) => {
          const team = teamsList.find(t => t.id === parseInt(tid));
          const names = members.map(p => p.name).join(" / ");
          const playerHcps = members.map(p => `${p.name.split(" ")[0]} (${p.hcp_adj ?? p.handicap})`).join(", ");
          const combHcp = members.reduce((s, p) => s + ((p.hcp_adj ?? p.handicap) || 0), 0);
          // Foursomes: 50% of combined Course Handicap (hcp_adj). Greensomes:
          // 60% of the lower + 40% of the higher — a genuinely different
          // allowance, not the same formula reused, per Rules of Handicapping.
          const isGreensomes = (eventRes.data.game_type || "").includes("Greensomes");
          const lowerHcp = Math.min(...members.map(p => (p.hcp_adj ?? p.handicap) || 0));
          const higherHcp = Math.max(...members.map(p => (p.hcp_adj ?? p.handicap) || 0));
          const allowance = isGreensomes
            ? Math.round(0.6 * lowerHcp + 0.4 * higherHcp)
            : Math.round(combHcp * 0.5);
          const mismatched = members.length !== 2;
          const droppedNames = members.slice(2).map(p => p.name);
          teamsArr.push({
            grp: parseInt(grp),
            teamId: parseInt(tid),
            teamName: team ? team.team_name : "Team",
            label: (mismatched ? "⚠️ " : "") + `Group ${grp}: ${names}`,
            names,
            playerHcps,
            combHcp,
            allowance,
            playerId: members[0].player_id,
            mismatched,
            droppedNames,
          });
        });
      });

      setTeams(teamsArr);
      if (teamsArr.length > 0) setSelectedTeam(teamsArr[0]);

      // Load existing scores
      const scoresRes = await api.get("/scores/" + eventRes.data.id);
      const map = {};
      scoresRes.data.forEach(s => { map[s.player_id + "_" + s.hole] = s.gross; });
      // Overlay any score already typed and queued (but not yet applied
      // server-side, e.g. entered while offline) so navigating away and
      // back never looks like it was lost.
      const pending = getQueuedScores(eventRes.data.id);
      Object.entries(pending).forEach(([pid, holeScores]) => {
        Object.entries(holeScores).forEach(([hole, gross]) => { map[pid + "_" + hole] = gross; });
      });
      setScores(map);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleScore = (hole, val) => {
    if (!selectedTeam) return;
    const key = selectedTeam.playerId + "_" + hole;
    setScores(prev => ({ ...prev, [key]: val }));
  };

  const getScore = (hole) => {
    if (!selectedTeam) return "";
    return scores[selectedTeam.playerId + "_" + hole] || "";
  };

  const getShots = (hole) => {
    if (!selectedTeam) return 0;
    const base = Math.floor(selectedTeam.allowance / 18);
    const extra = selectedTeam.allowance % 18;
    return base + (hole.stroke_index <= extra ? 1 : 0);
  };

  const isMedal = event?.game_type?.includes("Medal") || event?.game_type?.includes("Stroke");

  const getStats = (hole) => {
    const gross = parseInt(getScore(hole.hole));
    if (!gross) return { net: "", points: "", running: "" };
    const shots = getShots(hole);
    const net = gross - shots;
    const points = Math.max(0, 2 + (hole.par - net));
    return { net, points };
  };

  // Calculate running total
  const getRunningTotal = (upToIndex) => {
    let total = 0;
    for (let i = 0; i <= upToIndex; i++) {
      const h = holes[i];
      if (!h) continue;
      const gross = parseInt(getScore(h.hole));
      if (!gross) continue;
      if (isMedal) {
        total += gross;
      } else {
        const shots = getShots(h);
        const net = gross - shots;
        total += Math.max(0, 2 + (h.par - net));
      }
    }
    return total || "";
  };

  const saveScores = async () => {
    if (!event || !selectedTeam) return;
    try {
      const allScores = [];
      holes.forEach(h => {
        const gross = parseInt(getScore(h.hole));
        if (gross) allScores.push({ player_id: selectedTeam.playerId, hole: h.hole, gross });
      });
      const res = await api.post("/scores/save", { event_id: event.id, scores: allScores, publish: false });
      showToast(queuedMessage(res, "Scores saved!"));
    } catch(e) { showToast("Failed: " + e.message); }
  };

  const randomScores = () => {
    if (!selectedTeam) return;
    const newScores = { ...scores };
    holes.forEach(h => {
      newScores[selectedTeam.playerId + "_" + h.hole] = h.par + Math.floor(Math.random() * 3);
    });
    setScores(newScores);
    showToast("Random scores generated");
  };
  const randomAllTeams = async () => {
    if (!event || teams.length === 0) return;
    try {
      const allScores = [];
      teams.forEach(team => {
        holes.forEach(h => {
          allScores.push({ player_id: team.playerId, hole: h.hole, gross: h.par + Math.floor(Math.random() * 3) });
        });
      });
      const res = await api.post("/scores/save", { event_id: event.id, scores: allScores, publish: false });
      showToast(queuedMessage(res, "Random scores generated for all " + teams.length + " groups"));
      const scoresRes = await api.get("/scores/" + event.id);
      const map = {};
      scoresRes.data.forEach(s => { map[s.player_id + "_" + s.hole] = s.gross; });
      setScores(map);
    } catch(e) { showToast("Failed: " + e.message); }
  };

  const totalGross = holes.reduce((s, h) => s + (parseInt(getScore(h.hole)) || 0), 0);
  const totalPoints = isMedal ? "" : holes.reduce((s, h) => {
    const gross = parseInt(getScore(h.hole));
    if (!gross) return s;
    const shots = getShots(h);
    return s + Math.max(0, 2 + (h.par - (gross - shots)));
  }, 0);

  return (
    <Layout>
      {toast && <div style={{position:"fixed",bottom:"20px",right:"20px",zIndex:9999}} className="bg-green-600 text-white px-4 py-3 rounded shadow-lg text-sm font-medium">{toast}</div>}
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="font-semibold text-sm">Alternate Shot Score Entry</span>
          {event && <span className="text-xs text-blue-600 font-medium">{event.game_type}</span>}
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={saveScores} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Save Scores</button>
            <button type="button" onClick={randomScores} className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Random Scores</button>
            <button type="button" onClick={randomAllTeams} className="bg-pink-600 hover:bg-pink-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Random ALL Teams</button>
          </div>
        </div>

        <div className="bg-yellow-50 border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="text-xs font-medium text-gray-600">Select Team:</span>
          <select value={selectedTeam ? selectedTeam.label : ""}
            onChange={e => setSelectedTeam(teams.find(t => t.label === e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-xs w-72">
            {teams.map(t => <option key={t.label} value={t.label}>{t.label}</option>)}
          </select>
          {selectedTeam && (
            <span className="text-xs text-gray-600">
              Course HCP: <strong>{selectedTeam.playerHcps}</strong> &nbsp;|&nbsp; Combined Course HCP: <strong>{selectedTeam.combHcp}</strong> &nbsp;|&nbsp; Allowance: <strong>{selectedTeam.allowance}</strong>
            </span>
          )}
        </div>

        {selectedTeam?.mismatched && (
          <div className="bg-yellow-100 border-b border-yellow-300 px-4 py-2 text-xs text-yellow-800 font-medium">
            ⚠️ This group isn't split 2-and-2 by team — check team assignment on the Players page.
            {selectedTeam.droppedNames.length > 0 && ` Not getting scores entered here: ${selectedTeam.droppedNames.join(", ")}.`}
          </div>
        )}

        {loading && <div className="bg-blue-50 text-blue-700 px-4 py-2 text-sm">Loading...</div>}

        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-green-800 text-white">
              <tr>
                <th className="px-4 py-2 text-left w-16">Hole</th>
                <th className="px-4 py-2 text-center w-16">Par</th>
                <th className="px-4 py-2 text-center w-16">SI</th>
                <th className="px-4 py-2 text-center w-36">Gross</th>
                <th className="px-4 py-2 text-center w-16">HCP Shots</th>
                <th className="px-4 py-2 text-center w-16">Net</th>
                {!isMedal && <th className="px-4 py-2 text-center w-16">Points</th>}
                <th className="px-4 py-2 text-center w-24">Running</th>
              </tr>
            </thead>
            <tbody>
              {holes.map((hole, i) => {
                const { net, points } = getStats(hole);
                const shots = getShots(hole);
                const running = getRunningTotal(i);
                return (
                  <tr key={hole.hole} className={"border-b border-gray-200 " + (i%2===0 ? "bg-white" : "bg-green-50")}>
                    <td className="px-4 py-1.5 font-medium">{hole.hole}</td>
                    <td className="px-4 py-1.5 text-center">{hole.par}</td>
                    <td className="px-4 py-1.5 text-center text-gray-500">{hole.stroke_index}</td>
                    <td className="px-4 py-1.5 text-center">
                      <input type="number" min="1" max="15"
                        value={getScore(hole.hole)}
                        onChange={e => handleScore(hole.hole, e.target.value)}
                        className="border border-gray-300 rounded px-2 py-0.5 w-24 text-xs text-center focus:border-blue-500 focus:outline-none"
                      />
                    </td>
                    <td className="px-4 py-1.5 text-center font-medium text-green-700">{shots}</td>
                    <td className="px-4 py-1.5 text-center">{net}</td>
                    {!isMedal && <td className="px-4 py-1.5 text-center font-bold text-blue-700">{points}</td>}
                    <td className="px-4 py-1.5 text-center font-bold">{running}</td>
                  </tr>
                );
              })}
              <tr className="bg-green-800 text-white font-bold">
                <td className="px-4 py-2" colSpan={3}>TOTAL</td>
                <td className="px-4 py-2 text-center">{totalGross || ""}</td>
                <td className="px-4 py-2 text-center">{selectedTeam?.allowance || ""}</td>
                <td className="px-4 py-2 text-center">{totalGross && selectedTeam ? totalGross - selectedTeam.allowance : ""}</td>
                {!isMedal && <td className="px-4 py-2 text-center">{totalPoints || ""}</td>}
                <td className="px-4 py-2 text-center">{totalPoints || totalGross || ""}</td>
              </tr>
            </tbody>
          </table>
          {!loading && holes.length === 0 && (
            <div className="text-center py-8 text-gray-500">No course loaded. Go to Course page and select a course.</div>
          )}
          {!loading && teams.length === 0 && (
            <div className="text-center py-8 text-gray-500">No teams found. Go to Groups and Teams screens to set up players.</div>
          )}
        </div>
      </div>
    </Layout>
  );
}
